/**
 * Uniqueness advisory for the masking inventory editor (C2D-1129, constraint-aware since V32).
 *
 * A lookup-style algorithm maps many inputs onto a small closed list, so masking a
 * primary-key/unique column with one produces duplicate values and the job fails at
 * target-insert time with a duplicate-key error — deep in the run, long after the
 * assignment was made.
 *
 * Two evidence tiers:
 *  - CONFIRMED — the data source's Profile recorded the column under a real PRIMARY KEY /
 *    UNIQUE constraint (`keyConstraints` rides the discovery `fields[]` → descriptor → row).
 *    Single-column, non-partial, enforced constraints are the ones that WILL fail; composite
 *    membership, partial indexes and unenforced (informational) constraints get a softer note.
 *  - HEURISTIC — no Profile evidence for the column, so fall back to the original NAME-based
 *    guess: flag columns that look like their table's own key. Never blocks anything.
 *
 * The red banner at the top of the inventory lists only CONFIRMED conflicts (see
 * `collectInventoryIssues`), so it is never a false alarm; the per-row icon shows every level.
 */

// Baseline algorithms whose output is collision-free for distinct inputs: UNIQUE_ID by
// design, and the FPE family because format-preserving encryption is a bijection. Always
// honoured, even before the catalog loads or when the worker predates the metadata flag.
const UNIQUE_SAFE_ALGORITHMS = new Set(['UNIQUE_ID', 'FPE_UTF8', 'FPE_EBCDIC']);

// Names the masking worker itself reports with `preservesUniqueness: true` — custom JARs
// (e.g. a UNIQUE_EMAIL that forwards to UNIQUE_ID) and user aliases over a bijection.
// Filled by `registerAlgorithmCatalog` whenever a page loads the algorithm list; the
// advisory helpers below are called from deep inside the accordion without catalog
// context, so a module-level registry is the least invasive way to reach them.
let declaredUniqueSafe = new Set();

export const WARNING_LEVEL_CONFIRMED = 'confirmed';
export const WARNING_LEVEL_COMPOSITE = 'composite';
export const WARNING_LEVEL_HEURISTIC = 'heuristic';

const normId = (id) => String(id || '').trim().toUpperCase();

/**
 * Record which catalog algorithms the worker declares uniqueness-preserving. Pass the list
 * `listMaskingAlgorithms()` returns (each item may carry `preservesUniqueness`). Replaces the
 * previous registration; an empty list clears it. The baseline set is never affected.
 */
export function registerAlgorithmCatalog(algorithms) {
  const next = new Set();
  for (const a of Array.isArray(algorithms) ? algorithms : []) {
    if (a && a.preservesUniqueness === true && normId(a.id)) next.add(normId(a.id));
  }
  declaredUniqueSafe = next;
}

export function algorithmGuaranteesUniqueness(algorithmId) {
  const id = normId(algorithmId);
  return UNIQUE_SAFE_ALGORITHMS.has(id) || declaredUniqueSafe.has(id);
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * True when the field looks like its OWN table's key: `id` outright, or an `…id` name
 * whose stem appears in the table name (`profile_id` on custom_profiles, `CustomerID`
 * on Customers). A foreign key does not match — `customer_id` on orders has no
 * `customer` in `orders` — which is right, since FK columns don't require uniqueness.
 */
export function looksLikeKeyColumn(tableKey, fieldName) {
  const f = norm(fieldName);
  if (!f) return false;
  if (f === 'id' || f === 'uuid' || f === 'guid') return true;
  if (!f.endsWith('id') || f.length <= 2) return false;
  const stem = f.slice(0, -2);
  const table = norm(String(tableKey || '').split('.').pop());
  return table !== '' && table.includes(stem);
}

/** A constraint that requires every value in this column to be unique on its own. */
function requiresUniqueValue(ref) {
  return (
    ref &&
    Number(ref.columnCount) === 1 &&
    !ref.partial &&
    ref.enforced !== false
  );
}

const kindLabel = (ref) => (ref && ref.kind === 'PRIMARY_KEY' ? 'primary key' : 'unique constraint');

function confirmedMessage(ref, algorithm) {
  return (
    `This column is the ${kindLabel(ref)} \`${ref.name}\`, and ${algorithm} does not guarantee ` +
    'unique output — the masking job will fail with a duplicate-key error. ' +
    'Use Unique ID or FPE for key columns.'
  );
}

function softMessage(ref, algorithm) {
  if (ref.enforced === false) {
    return (
      `This column is declared in \`${ref.name}\`, which this engine does not enforce, and ` +
      `${algorithm} does not guarantee unique output. Duplicates will be accepted but the ` +
      'column may no longer identify rows.'
    );
  }
  if (ref.partial) {
    return (
      `This column is covered by the partial unique index \`${ref.name}\`, and ${algorithm} ` +
      'does not guarantee unique output — rows the index covers may collide.'
    );
  }
  return (
    `This column is part of the composite ${kindLabel(ref)} \`${ref.name}\` (${ref.columnCount} ` +
    `columns), and ${algorithm} does not guarantee unique output. Duplicates are possible only ` +
    'if the other key columns also collide.'
  );
}

/**
 * The advisory for one inventory row, or null when none applies (no algorithm chosen, a
 * uniqueness-safe algorithm, or no evidence the column requires unique values).
 *
 * @returns {null | { level: 'confirmed'|'composite'|'heuristic', message: string }}
 */
export function uniquenessWarning(tableKey, fieldName, algorithmId, { keyConstraints = null } = {}) {
  const algorithm = String(algorithmId || '').trim();
  if (!algorithm || algorithmGuaranteesUniqueness(algorithm)) return null;
  const refs = Array.isArray(keyConstraints) ? keyConstraints.filter(Boolean) : [];
  if (refs.length > 0) {
    const hard = refs.find(requiresUniqueValue);
    if (hard) return { level: WARNING_LEVEL_CONFIRMED, message: confirmedMessage(hard, algorithm) };
    // Prefer to explain a composite key over a partial/unenforced one when both apply.
    const soft = refs.find((r) => Number(r.columnCount) > 1 && !r.partial && r.enforced !== false) || refs[0];
    return { level: WARNING_LEVEL_COMPOSITE, message: softMessage(soft, algorithm) };
  }
  if (!looksLikeKeyColumn(tableKey, fieldName)) return null;
  return {
    level: WARNING_LEVEL_HEURISTIC,
    message:
      'This column looks like it requires unique values, and this algorithm does not ' +
      'guarantee unique output. If the column has a primary-key or unique constraint, ' +
      'the masking job may fail — Unique ID is a safe choice for key columns.',
  };
}

/** First "." splits schema from table, matching the accordion and the save side. */
function splitKey(tableKey) {
  const s = String(tableKey || '');
  const dot = s.indexOf('.');
  return dot > 0 ? { schema: s.slice(0, dot), table: s.slice(dot + 1) } : { schema: '', table: s };
}

/**
 * Confirmed conflicts across the whole inventory — the rows the red banner lists. Only
 * selected (checked) fields with an algorithm count; a de-selected field is not masked and
 * cannot fail. Iterates the same descriptors the accordion renders, so banner and icons
 * can never disagree.
 *
 * @returns {Array<{tableKey, schema, table, field, algorithm, level, message}>}
 */
export function collectInventoryIssues(selectedFields) {
  const issues = [];
  for (const tableKey of Object.keys(selectedFields || {})) {
    const fields = selectedFields[tableKey] || {};
    const { schema, table } = splitKey(tableKey);
    for (const field of Object.keys(fields)) {
      const fd = fields[field] || {};
      if (!fd.checked) continue;
      const algorithm = fd.algorithm != null ? String(fd.algorithm).trim() : '';
      if (!algorithm) continue;
      const warning = uniquenessWarning(tableKey, field, algorithm, { keyConstraints: fd.keyConstraints });
      if (warning && warning.level === WARNING_LEVEL_CONFIRMED) {
        issues.push({ tableKey, schema, table, field, algorithm, level: warning.level, message: warning.message });
      }
    }
  }
  return issues;
}
